/**
 * FASE2-WIZARD-2b-bis — Las tres dimensiones del mandato que se expresan como
 * intervalo, con todo lo que hace falta para pintarlas y para leerlas.
 *
 * Fuente ÚNICA: lo consumen el formulario del wizard (tres barras + texto
 * bajo cada una), el panel (valores iniciales en los extremos) y la ficha del
 * anuncio. Si viviera en varios sitios, publicar y leer acabarían diciendo
 * cosas distintas, que es justo lo que se quiere evitar.
 *
 * `messagePrefix` existe porque la unidad NO se coloca igual en las tres:
 *   - antigüedad y empleados la llevan al final ("De 2 a 20 años")
 *   - facturación la lleva en cada número ("De 250.000 € a 1,5 M€")
 * Con una sola plantilla genérica habría que escribir "De 2 años a 20 años".
 *
 * `formatBound` convierte la cota numérica en el texto que se inyecta.
 */

import {
  ANTIGUEDAD_SCALE,
  FACTURACION_SCALE,
  EBITDA_SCALE,
  EMPLEADOS_SCALE,
} from '../config/configListing';
import { formatFacturacionLimitK } from '../config/configFilters';

// FASE2-WIZARD-2b-ter: etiqueta CORTA de una frontera, para las marcas bajo
// la barra. Distinta de formatBound (el del texto del rango, que va suelto y
// puede permitirse "250.000 €"): bajo la barra hay 11 marcas en facturación y
// una etiqueta larga las hace ilegibles. 0 → "0", 250 → "250k", 1500 → "1,5M".
const formatFacturacionMarkK = k => {
  if (k === 0) return '0';
  if (k < 1000) return `${k}k`;
  const millones = k / 1000;
  return `${Number.isInteger(millones) ? millones : `${millones}`.replace('.', ',')}M`;
};

export const MANDATO_RANGE_GROUPS = [
  {
    id: 'antiguedad',
    scale: ANTIGUEDAD_SCALE,
    minName: 'pub_antiguedadIdxMin',
    maxName: 'pub_antiguedadIdxMax',
    minKey: 'antiguedadIdxMin',
    maxKey: 'antiguedadIdxMax',
    labelId: 'ListingPage.mandato.antiguedadLabel',
    messagePrefix: 'ListingPage.mandato.antiguedad',
    formatBound: v => `${v}`,
    formatMark: v => `${v}`,
  },
  {
    id: 'facturacion',
    scale: FACTURACION_SCALE,
    minName: 'pub_facturacionIdxMin',
    maxName: 'pub_facturacionIdxMax',
    minKey: 'facturacionIdxMin',
    maxKey: 'facturacionIdxMax',
    labelId: 'ListingPage.mandato.facturacionLabel',
    messagePrefix: 'ListingPage.mandato.facturacion',
    // Los límites están en miles de €; el formateador ya existía para los
    // selects Desde–Hasta del buscador: 250 → "250.000 €", 1500 → "1,5 M€".
    formatBound: formatFacturacionLimitK,
    formatMark: formatFacturacionMarkK,
  },
  {
    // FASE2-SCHEMA-01.5: EBITDA usa los MISMOS formateadores que facturación
    // —sus límites están en miles de € y la escala llega hasta 5 M€, dentro
    // del rango que ya cubren—. Los nombres dicen "facturacion" por dónde
    // nacieron, pero lo que formatean es un importe en miles.
    id: 'ebitda',
    scale: EBITDA_SCALE,
    minName: 'pub_ebitdaIdxMin',
    maxName: 'pub_ebitdaIdxMax',
    minKey: 'ebitdaIdxMin',
    maxKey: 'ebitdaIdxMax',
    labelId: 'ListingPage.mandato.ebitdaLabel',
    messagePrefix: 'ListingPage.mandato.ebitda',
    formatBound: formatFacturacionLimitK,
    formatMark: formatFacturacionMarkK,
  },
  {
    id: 'empleados',
    scale: EMPLEADOS_SCALE,
    minName: 'pub_empleadosMin',
    maxName: 'pub_empleadosMax',
    minKey: 'empleadosMin',
    maxKey: 'empleadosMax',
    labelId: 'ListingPage.mandato.empleadosLabel',
    messagePrefix: 'ListingPage.mandato.empleados',
    formatBound: v => `${v}`,
    formatMark: v => `${v}`,
  },
];

export const rangeGroupById = id => MANDATO_RANGE_GROUPS.find(g => g.id === id);

// ─── Fronteras ──────────────────────────────────────────────────────
// FASE2-WIZARD-2b-ter: los topes de la barra se posan sobre los CORTES
// entre tramos, no sobre los tramos. Con N tramos hay N+1 fronteras, y
// el rango elegido es lo que queda entre los dos cortes.
//
// POR QUÉ: con los topes sobre tramos, ambos podían caer en el mismo y
// un punto único acababa representando un rango entero ("De 2 a 5
// años"), que no se sostiene. Sobre fronteras eso es imposible por
// construcción: la separación mínima es un corte, o sea un tramo
// completo, así que siempre hay al menos un tramo seleccionado.
//
// EL DATO GUARDADO NO CAMBIA. La traducción vive aquí y solo aquí:
//   frontera fMin ─┐                      ┌─ frontera fMax
//                  ├── tramos fMin+1 … fMax ──┤
// Un corte en 2 y otro en 10 sobre la escala de antigüedad cubren los
// tramos 2 y 3, y es exactamente lo que se persiste: idxMin 2, idxMax 3.

// Ordinales de tramo (lo que se guarda) → posiciones de los dos topes.
export const ordinalsToBorders = (idxMin, idxMax) => [idxMin - 1, idxMax];

// Posiciones de los dos topes → ordinales de tramo (lo que se guarda).
export const bordersToOrdinals = (borderMin, borderMax) => [borderMin + 1, borderMax];

// Etiqueta de cada frontera. La frontera k (k < N) es el límite inferior del
// tramo k+1. La última no tiene techo y se marca solo con "+": esa frontera
// significa "sin límite superior", no el valor de la anterior otra vez, y
// repetirlo ("20 · 20+") se leía como un tropiezo.
//
// La asimetría con el extremo izquierdo es deliberada: abajo hay un suelo
// real —0 años, 0 €, 1 empleado— y arriba no lo hay, así que el izquierdo
// conserva su valor y no lleva ningún signo.
//
//   antigüedad   0 · 2 · 5 · 10 · 20 · +
//   empleados    1 · 6 · 16 · 51 · 101 · +
export const borderLabels = ({ scale = [], formatMark }) => [
  ...scale.map(t => formatMark(t.lowerBound)),
  '+',
];
